import { EVENT_CATALOGUE } from '@plancia/contracts';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { anyDate } from '@/lib/format/carrier-time';
import { dayMonth, dayMonthTime } from '@/lib/format/time';

// Event rows (07 §2, S30 «Timeline · righe»): facts solid, carrier forecasts
// dashed grey («previsto»), Plancia estimates dashed violet («stima»),
// withdrawn hollow and muted; «✓ AIS» when the position confirmed it.

export type TimelineKind = 'fact' | 'forecast' | 'estimate' | 'withdrawn';
export interface TimelineEvent {
  id: string;
  code: string;
  kind: TimelineKind;
  at: string;
  dateOnly?: boolean;
  /** Place as shown («Algeciras»). */
  place?: string | null;
  confirmedBy?: string[];
}

const GLYPH: Record<TimelineKind, string> = {
  fact: 'tg f',
  forecast: 'tg c',
  estimate: 'tg e',
  withdrawn: 'tg w',
};

/** The event's UI label (locales `event.<code>`), else the catalogue's label capitalised. */
export function eventLabel(code: string, t: TFunction, language: string): string {
  const def = EVENT_CATALOGUE.find((e) => e.code === code);
  const fallback = def ? (language === 'en' ? def.labelEn : def.labelIt) : code;
  return t(`event.${code}`, { defaultValue: fallback.charAt(0).toUpperCase() + fallback.slice(1) });
}

export function TimelineRow({ event }: { event: TimelineEvent }) {
  const { t, i18n } = useTranslation();
  // A carrier time as written; an instant (with its zone) in local time (ADR 0055).
  const date = anyDate(event.at) ?? new Date(Number.NaN);
  const label = [eventLabel(event.code, t, i18n.language), event.place].filter(Boolean).join(' · ');
  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: '84px 14px 1fr',
        gap: 10,
        padding: '6px 0',
        borderBottom: '1px solid var(--color-border)',
      }}
      data-kind={event.kind}
    >
      <span className="mono sub">{event.dateOnly ? dayMonth(date) : dayMonthTime(date)}</span>
      <span className={GLYPH[event.kind]} role="img" aria-label={t(`timeline.kind.${event.kind}`)} />
      <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
        <span className={event.kind === 'withdrawn' ? 'sub' : undefined}>{label}</span>
        {event.confirmedBy?.includes('ais') ? <span className="tag c-ok b-ok">✓ AIS</span> : null}
        {event.kind === 'forecast' ? <span className="tag">{t('timeline.forecast')}</span> : null}
        {event.kind === 'estimate' ? <span className="tag c-es b-es">{t('timeline.estimate')}</span> : null}
      </span>
    </div>
  );
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  const { t } = useTranslation();
  return (
    <ul aria-label={t('timeline.aria')}>
      {events.map((e) => (
        <li key={e.id}>
          <TimelineRow event={e} />
        </li>
      ))}
    </ul>
  );
}
